import { Info } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    ButtonGroup,
    NumberInput,
    ColorInput,
} from "../ui/index";

const POSITIONS = [
    { value: "right", label: "Right" },
    { value: "left", label: "Left" },
    { value: "top", label: "Top" },
    { value: "bottom", label: "Bottom" },
];

const SIZE_MODES = [
    { value: "auto",   label: "Auto" },
    { value: "custom", label: "Custom" },
    { value: "match",  label: "Match Image" },
];

const CURSOR_SIZE_MODES = [
    { value: "auto",   label: "Auto" },
    { value: "custom", label: "Custom" },
];

export default function ZoomWindowPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    if (settings.zoomType !== "window") {
        return (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-10 text-center">
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50">
                    <Info className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-sm font-semibold text-gray-800">
                    Zoom Window is inactive
                </p>
                <p className="mt-1.5 text-sm text-gray-400">
                    Go to the{" "}
                    <button
                        type="button"
                        onClick={() => actions.setTab("zoom")}
                        className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                    >
                        Zoom tab
                    </button>{" "}
                    and set the Display Mode to{" "}
                    <strong className="text-gray-600">Window</strong>.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <SectionCard
                title="Window Size"
                description="Dimensions of the floating zoom panel."
            >
                <SettingRow
                    label="Size Mode"
                    description="Auto fits the available space while preserving image aspect ratio. Match Image mirrors the main image exactly. Custom uses your defined dimensions."
                >
                    <ButtonGroup
                        options={SIZE_MODES}
                        value={settings.zoomWindowSizeMode}
                        onChange={(v) => s("zoomWindowSizeMode", v)}
                    />
                </SettingRow>

                {settings.zoomWindowSizeMode === "custom" && (
                    <>
                        <SettingRow
                            label="Width"
                            description="Width of the zoom window in pixels."
                        >
                            <NumberInput
                                value={settings.zoomWindowWidth}
                                onChange={(v) => s("zoomWindowWidth", v)}
                                min={100}
                                max={1200}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Height"
                            description="Height of the zoom window in pixels."
                        >
                            <NumberInput
                                value={settings.zoomWindowHeight}
                                onChange={(v) => s("zoomWindowHeight", v)}
                                min={100}
                                max={1200}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title="Position"
                description="Where the zoom window appears relative to the main image."
            >
                <SettingRow label="Placement">
                    <ButtonGroup
                        options={POSITIONS}
                        value={settings.zoomWindowPosition}
                        onChange={(v) => s("zoomWindowPosition", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Horizontal Offset"
                    description="Extra space between the image edge and the window."
                >
                    <NumberInput
                        value={settings.zoomWindowOffsetX}
                        onChange={(v) => s("zoomWindowOffsetX", v)}
                        min={-100}
                        max={200}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow
                    label="Vertical Offset"
                    description="Extra vertical space between the image edge and the window."
                >
                    <NumberInput
                        value={settings.zoomWindowOffsetY}
                        onChange={(v) => s("zoomWindowOffsetY", v)}
                        min={-100}
                        max={200}
                        unit="px"
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Fade Easing"
                description="Smooth the zoom window appearing and disappearing."
            >
                <SettingRow
                    label="Enable Fade"
                    description="Fade the zoom window in and out instead of snapping."
                >
                    <Toggle
                        value={settings.zoomWindowFadeEasing}
                        onChange={(v) => s("zoomWindowFadeEasing", v)}
                    />
                </SettingRow>

                {settings.zoomWindowFadeEasing && (
                    <SettingRow
                        label="Fade Duration"
                        description="How long the fade takes. Higher values are slower."
                    >
                        <NumberInput
                            value={settings.zoomWindowFadeEasingAmount}
                            onChange={(v) => s("zoomWindowFadeEasingAmount", v)}
                            min={50}
                            max={2000}
                            unit="ms"
                            width="w-24"
                        />
                    </SettingRow>
                )}
            </SectionCard>

            <SectionCard
                title="Window Style"
                description="Visual appearance of the zoom window panel."
            >
                <SettingRow
                    label="Border Radius"
                    description="Rounded corner radius of the window."
                >
                    <NumberInput
                        value={settings.zoomWindowBorderRadius}
                        onChange={(v) => s("zoomWindowBorderRadius", v)}
                        min={0}
                        max={50}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow label="Border Color">
                    <ColorInput
                        value={settings.zoomWindowBorderColor}
                        onChange={(v) => s("zoomWindowBorderColor", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Border Width"
                    description="Thickness of the window border."
                >
                    <NumberInput
                        value={settings.zoomWindowBorderWidth}
                        onChange={(v) => s("zoomWindowBorderWidth", v)}
                        min={0}
                        max={10}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow
                    label="Drop Shadow"
                    description="Apply a soft box shadow around the zoom window."
                >
                    <Toggle
                        value={settings.zoomWindowShadow}
                        onChange={(v) => s("zoomWindowShadow", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Cursor Overlay"
                description="Selection-box indicator drawn on the main image to show what area the zoom window is displaying."
            >
                <SettingRow
                    label="Show Cursor"
                    description="Display a selection box on the image while zooming."
                >
                    <Toggle
                        value={settings.zoomWindowCursor}
                        onChange={(v) => s("zoomWindowCursor", v)}
                    />
                </SettingRow>

                {settings.zoomWindowCursor && (
                    <>
                        <SettingRow
                            label="Size Mode"
                            description="Auto accurately represents the visible zoom area. Custom lets you set any fixed size."
                        >
                            <ButtonGroup
                                options={CURSOR_SIZE_MODES}
                                value={settings.zoomWindowCursorSizeMode}
                                onChange={(v) => s("zoomWindowCursorSizeMode", v)}
                            />
                        </SettingRow>

                        {settings.zoomWindowCursorSizeMode === "custom" && (
                            <>
                                <SettingRow
                                    label="Width"
                                    description="Fixed width of the cursor selection box."
                                >
                                    <NumberInput
                                        value={settings.zoomWindowCursorWidth}
                                        onChange={(v) => s("zoomWindowCursorWidth", v)}
                                        min={10}
                                        max={600}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>

                                <SettingRow
                                    label="Height"
                                    description="Fixed height of the cursor selection box."
                                >
                                    <NumberInput
                                        value={settings.zoomWindowCursorHeight}
                                        onChange={(v) => s("zoomWindowCursorHeight", v)}
                                        min={10}
                                        max={600}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>
                            </>
                        )}

                        <SettingRow
                            label="Border Radius"
                            description="Rounded corner radius of the cursor box. Use 50% of its size for a circle."
                        >
                            <NumberInput
                                value={settings.zoomWindowCursorBorderRadius}
                                onChange={(v) => s("zoomWindowCursorBorderRadius", v)}
                                min={0}
                                max={300}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Background"
                            description="Fill color inside the selection box. Use a semi-transparent color."
                        >
                            <ColorInput
                                value={settings.zoomWindowCursorBg}
                                onChange={(v) => s("zoomWindowCursorBg", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Border Color"
                            description="Color of the selection box border."
                        >
                            <ColorInput
                                value={settings.zoomWindowCursorBorderColor}
                                onChange={(v) => s("zoomWindowCursorBorderColor", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Border Width"
                            description="Thickness of the selection box border."
                        >
                            <NumberInput
                                value={settings.zoomWindowCursorBorderWidth}
                                onChange={(v) => s("zoomWindowCursorBorderWidth", v)}
                                min={0}
                                max={8}
                                unit="px"
                            />
                        </SettingRow>

                    </>
                )}
            </SectionCard>
        </div>
    );
}
